import React, { useState } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { iconNameFromValue, isImageUrl } from '../services/brandIcons';
import ActingOrgBanner from './ActingOrgBanner';

const Layout: React.FC = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const baseNavigation = [
    { name: '仪表盘', href: '/dashboard', icon: 'dashboard' },
    { name: '学校管理', href: '/schools', icon: 'school' },
    { name: '用户管理', href: '/user-search', icon: 'group' },
    { name: '预警监控', href: '/crisis-monitor', icon: 'warning' },
    { name: '问卷管理', href: '/questionnaires', icon: 'assignment' },
    { name: '题库管理', href: '/question-banks', icon: 'quiz' },
    { name: '数据导出', href: '/export', icon: 'download' },
    { name: '统计分析', href: '/site-statistics', icon: 'bar_chart' },
  ];

  // 机构与权限：仅超级管理员可见
  const ORG_NAV_ITEM = { name: '机构与权限', href: '/org-management', icon: 'admin_panel_settings' };
  const navigation = isSuperAdmin ? [...baseNavigation, ORG_NAV_ITEM] : baseNavigation;

  const bottomNavigation = [
    { name: '设置', href: '/settings', icon: 'settings' },
    { name: '帮助', href: '/help', icon: 'help' },
  ];

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <div className="min-h-screen bg-surface flex">
      {/* Sidebar */}
      <aside className="w-72 bg-surface-container-low flex flex-col flex-shrink-0">
        {/* Brand Area */}
        <div className="px-6 pt-8 pb-6">
          <div className="flex items-center space-x-3">
            {isImageUrl(branding.brandLogoUrl) ? (
              <img
                src={branding.brandLogoUrl as string}
                alt={branding.brandName}
                className="w-10 h-10 rounded-2xl object-cover shadow-teal"
              />
            ) : (
              <div className="w-10 h-10 rounded-2xl gradient-primary flex items-center justify-center shadow-teal">
                <span className="material-symbols-outlined text-on-primary" style={{ fontSize: '22px' }}>
                  {iconNameFromValue(branding.brandLogoUrl) ?? 'psychology'}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-base font-bold text-on-surface font-headline tracking-tight">{branding.brandName}</h1>
              <p className="text-xs text-on-surface-variant font-label">{branding.brandSubtitle}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 space-y-1">
          <div className="text-[11px] font-semibold text-on-surface-variant uppercase tracking-wider px-3 mb-3 font-label">
            菜单
          </div>
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2.5 text-sm rounded-xl transition-all duration-200 ${
                  active
                    ? 'bg-white/60 text-primary-700 font-semibold shadow-soft'
                    : 'text-on-surface-variant hover:bg-white/40 hover:text-on-surface'
                }`}
              >
                <span className="material-symbols-outlined mr-3" style={{ fontSize: '20px' }}>{item.icon}</span>
                <span className="font-body">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Bottom Navigation */}
        <div className="px-4 pb-2 space-y-1">
          {bottomNavigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2.5 text-sm rounded-xl transition-all duration-200 ${
                  active
                    ? 'bg-white/60 text-primary-700 font-semibold shadow-soft'
                    : 'text-on-surface-variant hover:bg-white/40 hover:text-on-surface'
                }`}
              >
                <span className="material-symbols-outlined mr-3" style={{ fontSize: '20px' }}>{item.icon}</span>
                <span className="font-body">{item.name}</span>
              </Link>
            );
          })}
        </div>

        {/* User Profile */}
        <div className="p-4 mx-4 mb-4 rounded-2xl bg-white/50">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 gradient-primary rounded-full flex items-center justify-center text-on-primary font-semibold text-sm">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-on-surface truncate font-body">
                {user?.username}
              </p>
              <p className="text-xs text-on-surface-variant truncate font-label">管理员</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-3 py-2 text-sm text-on-surface-variant hover:bg-surface-container-high rounded-xl transition-colors flex items-center justify-center space-x-2 font-body"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>logout</span>
            <span>退出登录</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Acting Org Bar */}
        <ActingOrgBanner />

        {/* Top Header Bar */}
        <header className="h-16 bg-surface-container-lowest flex items-center justify-between px-8 flex-shrink-0 shadow-soft">
          {/* Search */}
          <div className="flex-1 max-w-xl">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" style={{ fontSize: '20px' }}>search</span>
              <input
                type="text"
                placeholder="搜索..."
                className="w-full pl-10 pr-4 py-2.5 bg-surface-container-low rounded-xl text-sm text-on-surface placeholder:text-outline-variant focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all font-body"
              />
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-3 ml-6">
            {/* Notifications */}
            <button className="relative p-2.5 rounded-xl hover:bg-surface-container-low transition-colors">
              <span className="material-symbols-outlined text-on-surface-variant" style={{ fontSize: '22px' }}>notifications</span>
              <span className="absolute top-2 right-2 w-2 h-2 bg-error rounded-full"></span>
            </button>

            {/* Settings */}
            <button
              onClick={() => navigate('/settings')}
              className="p-2.5 rounded-xl hover:bg-surface-container-low transition-colors"
            >
              <span className="material-symbols-outlined text-on-surface-variant" style={{ fontSize: '22px' }}>settings</span>
            </button>

            {/* User */}
            <div className="flex items-center space-x-3 pl-3 ml-3" style={{ borderLeft: '1px solid rgba(171, 179, 183, 0.3)' }}>
              <div className="w-9 h-9 gradient-primary rounded-full flex items-center justify-center text-on-primary font-semibold text-sm">
                {user?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-on-surface font-body">{user?.username}</p>
                <p className="text-xs text-on-surface-variant font-label">管理员</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto" style={{ scrollbarGutter: 'stable' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
